/**
 * Balance Page
 * Display policy balance and payment entry
 */

import { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { type BalanceResponse } from '@/types/api.types';
import { usePaymentStore } from '@/store/paymentStore';
import { usePolicyNumber, usePaymentType, useAuthStore } from '@/store/authStore';
import { getBalance } from '@/api/services/balanceService';
import { LoadingSpinner, ErrorMessage, Button, Card, CardHeader, CardTitle } from '@/components/common';
import { BalanceSummary, PaymentAmountInput, StatementButton } from '@/components/balance';
import { ROUTE_PATHS, ERROR_MESSAGES } from '@/utils/constants';

export default function BalancePage() {
  const navigate = useNavigate();
  const policyNumber = usePolicyNumber();
  const paymentType = usePaymentType();
  const setBalance = usePaymentStore((state) => state.setBalance);
  const logout = useAuthStore((state) => state.logout);
  const clearPaymentData = usePaymentStore((state) => state.clearPaymentData);

  const [balanceData, setBalanceData] = useState<BalanceResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const handleBack = () => {
    // Clear all client-side state
    logout();
    clearPaymentData();
    // Navigate to login page
    navigate(ROUTE_PATHS.LOGIN, { replace: true });
  };

  useEffect(() => {
    const controller = new AbortController();

    const fetchBalance = async () => {
      if (!paymentType) {
        setError('Payment type is not available. Please log in again.');
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const data = await getBalance(paymentType, controller.signal);
        setBalanceData(data);
        setBalance(data); // Store in payment store for later use
      } catch (err) {
        if (controller.signal.aborted) return;

        console.error('Failed to fetch balance:', err);

        if (err instanceof AxiosError) {
          if (err.response?.status === 401) {
            // Unauthorized - redirect to login
            navigate(ROUTE_PATHS.LOGIN, { replace: true });
          } else if (err.response?.status === 404) {
            setError('Balance information not found. Please contact support.');
          } else if (err.response?.status === 500) {
            setError(ERROR_MESSAGES.GENERIC);
          } else {
            setError('Failed to load balance information. Please try again.');
          }
        } else {
          setError(ERROR_MESSAGES.NETWORK);
        }
      } finally {
        if (!controller.signal.aborted) {
          setIsLoading(false);
        }
      }
    };

    fetchBalance();

    return () => controller.abort();
  }, [navigate, setBalance, paymentType]);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex justify-center items-center min-h-[300px]">
        <LoadingSpinner size="lg" message="Loading balance information..." />
      </div>
    );
  }

  // Error state
  if (error || !balanceData) {
    return (
      <div className="max-w-2xl mx-auto">
        <ErrorMessage
          message={error || 'Unable to load balance information'}
          variant="card"
          onRetry={() => window.location.reload()}
        />
        <div className="mt-4">
          <Button variant="ghost" onClick={handleBack}>
            &larr; Back to Login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Back Navigation */}
      <div>
        <Button variant="ghost" onClick={handleBack} size="sm">
          &larr; Back to Login
        </Button>
      </div>

      {/* Page Header with Statement Button */}
      <Card>
        <CardHeader
          action={paymentType === 'Deductible' ? <StatementButton /> : undefined}
        >
          <CardTitle>Balance Summary</CardTitle>
          <p className="text-sm text-text-muted mt-1">
            Policy: {policyNumber} | Type: {paymentType === 'Deductible' ? 'Deductible' : 'Audit Premium'}
          </p>
        </CardHeader>
      </Card>

      {/* Balance Summary */}
      <BalanceSummary balance={balanceData} />

      {/* Payment Amount Input */}
      <PaymentAmountInput
        balance={balanceData.totalBalance}
        minimumPayment={0}
        defaultAmount={balanceData.totalBalance}
        onBack={handleBack}
      />
    </div>
  );
}
